import React, { useState, useEffect, useRef } from "react";
import sgBg from './assets/sg_bg.jpeg';
import swotLogo from './assets/SWOT_logo.jpeg';
import umesh from './assets/Umesh_photo.jpg';
import rohit from './assets/Rohit_photo.jpg';
import mohit from './assets/Mohit_photo.jpg';
import navin from './assets/Navin_photo.jpg';
import pinki from './assets/Pinki_photo.png';
import piyush from './assets/Piyush_photo.jpg';
import puneet from './assets/Puneet_photo.jpg';
import skagarwal from './assets/SKAgarwal_photo.jpeg';
import wamika from './assets/Wamika_photo.jpeg';
import megha from './assets/Megha_photo.jpeg';

// ─── Singapore background: Lion city / Marina Bay skyline feel via gradient
// Replace with: import sgBg from './assets/sg_bg.jpeg'; and use in hero-bg
const SG_BG_GRADIENT = "linear-gradient(135deg, #0a1628 0%, #102040 60%, #1a3055 100%)";

// ─── DATA ────────────────────────────────────────────────────────────────────

const NAV = [
  { id: "home", label: "Home" },
  { id: "about", label: "About", sub: [
    { id: "about-who", label: "About SWOT Singapore" },
    { id: "about-vision", label: "Vision, Mission & Values" },
    { id: "about-founders", label: "Founders / Promoters" },
    { id: "about-board", label: "Board of Directors & Advisors" },
    { id: "about-why", label: "Why SWOT" },
    { id: "about-quality", label: "Quality & Integrity" },
  ]},
  { id: "services", label: "Services", sub: [
    { id: "svc-crossborder", label: "Cross-Border Tax & Structuring" },
    { id: "svc-corporate", label: "Corporate Compliance & Secretarial" },
    { id: "svc-operations", label: "Business Operations & Shared Services" },
    { id: "svc-cfo", label: "CFO Advisory, Finance & MIS" },
    { id: "svc-accounting", label: "Accounting, Payroll & HR" },
    { id: "svc-tax", label: "Tax, Regulatory & Corporate Documentation" },
    { id: "svc-setup", label: "Market Entry & Business Set-up" },
    { id: "svc-banking", label: "Banking, Funding & Treasury" },
    { id: "svc-process", label: "Process, Controls, ERP & Transformation" },
    { id: "svc-family", label: "Family Office & Succession" },
    { id: "svc-valuation", label: "Valuation, Transaction & Investor Readiness" },
    { id: "svc-training", label: "Training & Workshops" },
  ]},
  { id: "industries", label: "Industries" },
  { id: "consulting-method", label: "Consulting Method" },
  { id: "testimonials", label: "Testimonials" },
  { id: "insights", label: "Insights" },
  { id: "careers", label: "Careers" },
  { id: "contact", label: "Contact" },
];

const SERVICES_OVERVIEW = [
  { id: "svc-crossborder", icon: "🌐", title: "Cross-Border Tax and Structuring", desc: "Inbound/outbound structuring, treaty analysis, PE/POEM/SEP review, transfer pricing, withholding, repatriation and cross-border transaction planning." },
  { id: "svc-corporate", icon: "📋", title: "Corporate Compliance and Secretarial Support", desc: "Singapore company incorporation, ACRA filings, board and shareholder documentation, statutory records, annual compliance, corporate actions and governance support." },
  { id: "svc-operations", icon: "⚙️", title: "Business Operations and Shared Services", desc: "Operational coordination, documentation processing, vendor coordination, workflow management and back-office support." },
  { id: "svc-cfo", icon: "📊", title: "CFO Advisory, Finance Support and MIS", desc: "Budgeting, forecasting, FP&A, KPI dashboards, monthly reporting, treasury review and finance-function support." },
  { id: "svc-accounting", icon: "📑", title: "Accounting, Bookkeeping, Payroll and HR", desc: "Bookkeeping, reconciliations, payroll coordination, HR records, employee administration and compliance support." },
  { id: "svc-tax", icon: "🏛", title: "Tax, Regulatory and Corporate Documentation", desc: "Direct and indirect tax support, corporate documentation, regulatory filings and representation assistance where permitted." },
  { id: "svc-setup", icon: "🚀", title: "Market Entry and Establishment", desc: "Set-up coordination, registrations, banking support, office establishment, vendor onboarding and compliance calendar support." },
  { id: "svc-banking", icon: "🏦", title: "Banking, Funding and Treasury", desc: "Project reports, data / reports as per bank's mandate, financial projections, lender packs, cash-flow review and treasury process support." },
  { id: "svc-process", icon: "🔄", title: "Process, Controls, ERP and Transformation", desc: "SOPs, internal controls, ERP/process implementation, reporting automation, workflow digitisation and transformation support." },
  { id: "svc-family", icon: "🏠", title: "Family Office, Succession and HNI Advisory", desc: "Family office, succession, estate planning coordination, holding structure design, reporting, governance and multi-jurisdictional wealth documentation support." },
  { id: "svc-valuation", icon: "📈", title: "Valuation, Transaction and Investor Readiness", desc: "Valuation support, transaction assistance, financial models, investor decks, data rooms and due-diligence response support." },
  { id: "svc-training", icon: "🎓", title: "Training and Workshops", desc: "Training for finance, operations and compliance teams on reporting, controls, ERP, GST, Exchange Control Regulations, and process discipline." },
];

const SERVICE_DETAILS = {
  "svc-crossborder": {
    title: "Cross-Border Tax and Structuring",
    intro: "SWOT supports clients on inbound, outbound and multi-jurisdictional tax and structuring matters. The work is designed for businesses, investors, family offices and multinational groups that operate across borders and need coordinated tax, regulatory and commercial analysis.",
    items: [
      ["Singapore Market-entry Structuring", "Entity selection, Singapore company set-up planning, shareholder structure review, tax-residency considerations and business-entry support for foreign investors."],
      ["Treaty and Cross-border Tax Review", "Review of Singapore's tax treaty network, treaty-relief eligibility, withholding-tax implications and cross-border payment structures."],
      ["Tax Residency and Substance Review", "Review of Singapore tax-residency position, board-control framework, management substance, economic presence and documentation support."],
      ["Transfer Pricing Support", "Transfer-pricing documentation, benchmarking coordination, related-party transaction review and master file / local file support, where applicable."],
      ["International Tax and Anti-avoidance Review", "Review of BEPS principles, anti-avoidance considerations, beneficial ownership, treaty-access conditions and substance requirements."],
      ["Withholding Tax and Cross-border Payments", "Singapore withholding-tax review for royalties, interest, service fees and other cross-border payments, including documentation and compliance coordination."],
      ["Cross-border Documentation Support", "Inter-company agreements, board notes, management-substance records, transaction papers and documentation support in coordination with legal counsel, where required."],
    ]
  },
  "svc-corporate": {
    title: "Corporate Compliance and Secretarial Support",
    intro: "SWOT supports companies with incorporation, ACRA filings, corporate governance documentation, statutory records, annual compliance and entity-management support. Where Singapore law requires a company secretary, registered filing agent, legal counsel or other eligible professional, the work is coordinated through or reviewed by such professionals.",
    items: [
      ["Singapore Companies Act Compliance", "Support for board, shareholder and statutory compliance under Singapore company law, including compliance calendars and entity records."],
      ["ACRA Filing Support", "Assistance with annual returns, company information updates, director/shareholder changes, share capital changes and other ACRA-related filings."],
      ["Board and Shareholder Documentation", "Preparation support for notices, agendas, minutes, board resolutions, shareholder resolutions and corporate policies."],
      ["Company Incorporation and Set-up", "Singapore company incorporation support, constitution documentation, registered office coordination and post-incorporation compliance assistance."],
      ["Share Capital and Corporate Actions", "Support for share allotments, transfers, capital changes, shareholder records, name changes and other corporate events."],
      ["Group Entity Management", "Entity registers, group-compliance trackers, corporate records, document repository and governance reporting support."],
      ["Governance Advisory", "Governance frameworks, delegation matrices, policy documents, board processes and internal approval structures."],
    ]
  },
  "svc-operations": {
    title: "Business Operations and Shared Services",
    intro: "SWOT assists businesses in streamlining administrative and operational workflows through documented processes, defined responsibilities and scalable support structures. The work supports businesses that require better coordination, improved turnaround and reliable back-office execution.",
    items: [
      ["Operational Coordination", "Day-to-day coordination across finance, administration, vendors and reporting stakeholders."],
      ["Process Improvement", "Review of existing workflows, identification of delays and implementation of practical improvements."],
      ["SOP Documentation", "Preparation and update of SOPs, checklists, process maps and responsibility matrices."],
      ["Workflow Management", "Tracking of tasks, approvals, documents, escalations and reporting timelines."],
      ["Administrative Shared Services", "Documentation processing, vendor coordination, reporting support and back-office assistance."],
      ["Managed Compliance Desk", "Compliance calendars, filing trackers, document repositories and reminder mechanisms."],
    ]
  },
  "svc-cfo": {
    title: "CFO Advisory, Finance Support and MIS",
    intro: "SWOT partners with businesses to strengthen financial visibility and decision making through strategic finance support, management reporting, forecasting, cash-flow review and finance-function support. Our services are designed for leadership teams seeking clearer visibility into profitability, working capital, cost management, operational risks and growth performance.",
    items: [
      ["CFO Advisory", "Strategic finance support for owners, founders, boards and leadership teams."],
      ["FP&A", "Long term planning, Budgeting, forecasting, Capex - project portfolio management support, variance analysis, rolling forecast and scenario analysis."],
      ["MIS and Reporting", "Monthly reporting packs, KPI dashboards, Business intelligence (BI-BO) – Dashboards, profitability analysis and management summaries."],
      ["Cash-flow and Working Capital", "Cash-flow planning, receivable/payable review and working-capital visibility."],
      ["Treasury Support", "Treasury process review, bank coordination support and liquidity monitoring."],
      ["Finance Function Structuring", "Role clarity, reporting calendars, review mechanisms and finance process design."],
    ]
  },
  "svc-accounting": {
    title: "Accounting, Bookkeeping, Payroll and HR Support",
    intro: "SWOT assists businesses with accounting, bookkeeping, payroll coordination, HR documentation and workforce administration support. The service is structured for organisations that need reliable finance and HR operations without expanding internal administrative load.",
    items: [
      ["Accounting and Bookkeeping", "Books maintenance, reconciliations, schedules, closing support and financial statement preparation assistance."],
      ["Management Reporting", "Periodic reporting packs, ageing summaries, variance notes and financial summaries."],
      ["Payroll Coordination", "Payroll inputs, payroll processing coordination, reimbursements and statutory deduction support."],
      ["HR Documentation", "Employee records, policy documentation, onboarding checklists and HR process support."],
      ["Workforce Administration", "Employee administration, attendance inputs, documentation processing and workflow tracking."],
      ["Support for Labour Laws", "Documentation, registers, policy support, reporting assistance and coordination with relevant professionals."],
    ]
  },
  "svc-tax": {
    title: "Tax, Regulatory and Corporate Documentation Support",
    intro: "SWOT assists businesses with tax, corporate, regulatory and cross-border documentation support. The services include preparation of replies, filings, applications, registers, compliance trackers and supporting schedules, together with coordination with qualified professionals and external counsel where required.",
    items: [
      ["Direct and Indirect Tax Support", "Tax and GST compliance support, reconciliations, reply preparation, assessment support and advisory coordination."],
      ["Corporate Compliance", "Company and LLP documentation, statutory records, board documentation, annual filings and event-based filing support."],
      ["Exchange Control managed by Monetary Authority of Singapore linked Support", "Documentation and filing support for India-linked foreign investment, remittance and cross-border transactions."],
      ["Regulatory Representation Support", "Reply preparation, submissions, document review and representation assistance wherever permitted."],
      ["Contract and Board Documentation", "Drafting support for board packs, management notes, policy documents and strategic documents."],
      ["Compliance Calendar", "Tracking of due dates, document status, filing responsibilities and management reporting."],
    ]
  },
  "svc-setup": {
    title: "Market Entry, Business Set-up and Establishment Support",
    intro: "SWOT assists domestic and international businesses with market-entry planning, entity set-up, registrations, banking coordination, regulatory filings and post-incorporation compliance support. The services include company or LLP incorporation support, tax registrations, APAC or India-linked regulatory support, office establishment coordination and ongoing compliance assistance.",
    items: [
      ["Market-entry Planning", "Entity-structure inputs, jurisdiction coordination, set-up timelines and compliance roadmap."],
      ["Entity Set-up Coordination", "Company, subsidiary, LLP, branch or representative-office set-up support through relevant professionals."],
      ["Registrations", "Tax, GST, import-export and business registration support as applicable."],
      ["Banking Coordination", "Bank-account documentation, KYC pack support and banking follow-up coordination."],
      ["Office Establishment", "Office set-up checklists, vendor onboarding, process documentation and local administration support."],
      ["Post-establishment Compliance", "Compliance calendar, recurring filings, accounting support and reporting processes."],
    ]
  },
  "svc-banking": {
    title: "Banking, Funding Support and Treasury Advisory",
    intro: "SWOT assists businesses with banking documentation, funding-readiness support, cash-flow planning and treasury process review. The work includes project reports, Bank mandated Data / Reports, financial projections, lender packs, bank-query support, working-capital assessment and treasury reporting assistance.",
    items: [
      ["Funding Documentation", "Project reports, business plans, financial projections and lender information packs."],
      ["Bank Query Support", "Preparation of replies, schedules and supporting documentation for bank discussions."],
      ["Bank mandated data / reports and Working Capital", "Bank mandated data / reports preparation, working-capital assessment and cash-conversion-cycle analysis."],
      ["Cash-flow Planning", "Cash-flow forecasts, liquidity summaries and scenario-based review."],
      ["Treasury Process Review", "Banking process review, payment controls, authorisation matrices and treasury reporting."],
      ["Debt Restructuring Analysis", "Financial analysis, repayment schedules, scenario notes and documentation support."],
    ]
  },
  "svc-process": {
    title: "Process, Controls, ERP and Business Transformation",
    intro: "SWOT assists businesses in reviewing and improving finance, accounting and operational processes through SOPs, internal-control frameworks, ERP/process implementation support, automation, management reporting dashboards and workflow redesign. The objective is to enhance operational efficiency, improve documentation standards, strengthen control discipline and provide clearer, more actionable management reporting for better decision making.",
    items: [
      ["SOP Development", "SOPs, process maps, checklists, review calendars and responsibility matrices."],
      ["Internal Controls", "Control design, approval matrices, maker-checker workflows and review documentation."],
      ["ERP Coordination", "Software selection support, implementation coordination, User Acceptance Testing (UAT) support and process alignment."],
      ["Automation and Dashboards", "Reporting automation, dashboard coordination, data templates and workflow digitisation."],
      ["Process Re-engineering", "Review of existing workflows and redesign of processes to improve turnaround time, operational efficiency and accountability."],
      ["Transformation PMO Support", "Project trackers, meeting notes, implementation milestones and issue-resolution follow-up."],
    ]
  },
  "svc-family": {
    title: "Family Office, Succession and HNI Advisory",
    intro: "SWOT Singapore supports family offices, promoters and HNI families through structured reporting, entity-management, succession coordination, family governance and cross-border wealth documentation. Legal drafting, wills, trusts and court-related matters are coordinated with legal counsel or other eligible professionals wherever required.",
    items: [
      ["Family office reporting", "Consolidated reporting packs, asset and liability schedules, investment records and family-office dashboards."],
      ["Holding structure design", "Review of family holding companies, investment entities, trusts and international structures from tax, regulatory and reporting perspectives."],
      ["Succession coordination", "Coordination with legal counsel on wills, trusts, family arrangements and generational transition planning."],
      ["Family governance", "Family charters, decision frameworks, investment reporting protocols and documentation controls."],
      ["Estate and settlement support", "Financial, tax and documentation support for estate planning and family-settlement matters."],
      ["Cross-border family wealth", "Support on remittance, offshore entity, reporting, documentation and tax considerations for multi-jurisdictional families."],
    ]
  },
  "svc-valuation": {
    title: "Valuation, Transaction and Investor-Readiness Support",
    intro: "SWOT assists businesses with financial analysis and documentation for valuation, transaction planning and investor-readiness assignments. The services are design to support strategic decision-making, internal approvals, investor discussions and due-diligence preparedness through structured analysis, financial documentation, and transaction support materials.",
    items: [
      ["Valuation Support", "Business valuation, share valuation, asset/liability analysis and financial modelling support."],
      ["Transaction Support", "Buy-side and sell-side information support, financial analysis and closing-documentation assistance."],
      ["Investor Readiness", "Investor decks, data rooms, financial models, KPI packs and due-diligence response coordination."],
      ["Due Diligence Coordination", "Information request tracking, data room indexing, query responses and financial schedules."],
      ["ESOP Support", "ESOP documentation inputs, valuation coordination, accounting inputs and reporting support."],
      ["Business Research", "Sector notes, pricing analysis, competitor scans, benchmarking and decision-support research."],
    ]
  },
  "svc-training": {
    title: "Training and Workshops",
    intro: "SWOT designs and delivers practical training sessions and workshops for finance, operations and compliance teams. The programmes are structured around real business scenarios, reporting requirements and regulatory frameworks relevant to Singapore and APAC markets.",
    items: [
      ["Finance and Reporting Training", "MIS design, FP&A fundamentals, dashboard interpretation, variance analysis and financial storytelling for non-finance teams."],
      ["GST and Tax Compliance", "Practical GST compliance, filing, reconciliation and common error correction for operations and accounts teams."],
      ["Exchange Control Regulations", "Understanding cross-border payments, MAS guidelines, remittance documentation and compliance obligations."],
      ["ERP and Process Workshops", "Hands-on ERP usage, process mapping, SOP design and workflow documentation workshops."],
      ["Internal Controls Training", "Control frameworks, audit readiness, maker-checker protocols and internal review processes."],
      ["Compliance and Corporate Governance", "Corporate secretarial basics, board documentation, ACRA filing awareness and governance best practices."],
    ]
  },
};

const INDUSTRIES = [
  { icon: "🏭", group: "Manufacturing and Heavy Industry", sectors: "Industrial manufacturing, steel, engineering, automotive components, process industries and heavy industry businesses." },
  { icon: "💻", group: "Media, Digital and Technology", sectors: "Media, digital businesses, SaaS, software services, platforms, online businesses and technology-enabled operations." },
  { icon: "📦", group: "Trading and Distribution", sectors: "Import-export, trading, wholesale distribution, logistics, supply chain and vendor-led businesses." },
  { icon: "💼", group: "Financial and Professional Services", sectors: "Finance-led businesses, consultants, service firms, family offices and professional services platforms." },
  { icon: "🏥", group: "Healthcare, Hospitality and Consumer", sectors: "Healthcare, diagnostics, hotels, travel, consumer brands, retail and lifestyle businesses." },
  { icon: "🏗", group: "Real Estate and Infrastructure", sectors: "Real estate, construction, infrastructure support businesses and project-based entities." },
];

const WHY_SWOT = [
  { title: "About Three Decades of Continuity", desc: "Established in 1997, SWOT brings nearly 3 decades of unbroken professional continuity, institutional memory and disciplined practice management — a track record that few independent firms can match." },
  { title: "Qualified Professional Leadership", desc: "Engagements are led and supported by a multidisciplinary team with deep functional expertise across finance, taxation, corporate law and regulatory practice. The Company itself brings about 3 decades of dedicated experience in cross-border transactions, corporate governance, regulatory representation and complex advisory." },
  { title: "Cross-Border Capability, Both Directions", desc: "Genuine, hands-on capability for inbound investment into India and outbound expansion from India — supported by treaty fluency, regulatory depth and a working network across key jurisdictions." },
  { title: "Integrated, Single-Accountability Model", desc: "A single advisory platform covering tax, regulatory, secretarial, Exchange Control Regulations managed by Central Banks, e.g., RBI in India and Monetary Authority of Singapore (MAS), Virtual CFO and transaction needs — eliminating coordination friction across multiple service providers." },
  { title: "Boutique Attention. Institutional Discipline.", desc: "The personalised attention of an independent firm, delivered through institutional-grade quality controls, documentation standards and confidentiality protocols." },
  { title: "Independence", desc: "An unwavering commitment to professional independence and freedom from conflicts of interest — every engagement assessed and accepted on the basis of formal independence checks." },
  { title: "Technology-Enabled Delivery", desc: "Cloud accounting platforms, secure data rooms, encrypted communications, and automated compliance trackers ensure accuracy, efficiency, security and timeliness of delivery." },
  { title: "Fee Transparency", desc: "Clear, upfront engagement letters; defined scope; transparent commercial arrangement with no surprise billing. Our clients know what they are paying for, and what they will receive in return." },
];

const FOUNDERS = [
  {
    name: "Umesh Gupta",
    photo: umesh,
    quals: "B.Com.(H), FCA, FCS, Grad-CWA",
    bio: "Senior finance professional with over 30 years of experience across finance, tax, corporate compliance, regulatory representation, internal controls, SOP development, business process systemisation, business valuation, due diligence, transaction advisory, financial structuring, business plans, strategies and project appraisals. His experience includes representation before authorities such as RBI, DGFT, SEBI, Registrar of Companies, tax authorities and appellate forums, together with drafting of strategic documents, settlements and compliance documentation.",
    linkedin: "https://www.linkedin.com/in/umesh-gupta-498152117/",
    role: "Founder / Promoter",
  },
  {
    name: "Rohit Malik",
    photo: rohit,
    quals: "B.Com. (H), Certified Management Accountant (CMA)",
    bio: "Seasoned finance leader with over 30 years of experience partnering with businesses across manufacturing, heavy industry, media and digital sectors. His expertise spans CFO advisory, FP&A, performance management, long term planning, budgeting, forecasting, treasury, cost optimisation, governance frameworks, ERP/SAP transformation and finance automation. He supports business owners and leadership teams with stronger financial controls, improved profitability, build scalable processes and enable data-driven decision-making through structural financial and operational management support.",
    linkedin: "https://www.linkedin.com/in/rohit-malik-4a059115/",
    role: "Founder / Promoter",
  },
];

const BOARD = [
  {
    name: "S. K. Agrawal",
    photo: skagarwal,
    quals: "B.Com., FCA",
    role: "Independent Director",
    bio: "CA S. K. Agrawal has over 54 years of composite experience in the field of Audit, Taxation and Due Diligence. Over the years he has developed special expertise in the Banking and Financial Services Industry. He has experience in servicing large and medium sized clients in the areas of Internal Audit, Concurrent Audit, IT Advisory, Taxation, Risk Management and Management Consultancy. He is Partner in a legacy accounting firm (N D Kapur & Co., Chartered Accountants) operating in India for more than 90 years.",
    linkedin: "https://www.linkedin.com/in/sushil-kumar-agrawal-792bb947/",
  },
  {
    name: "Umesh Gupta",
    photo: umesh,
    quals: "B.Com.(H), FCA, FCS, Grad-CWA",
    role: "Independent Director",
    bio: "Senior finance professional with over 30 years of experience across finance, tax, corporate compliance, regulatory representation, internal controls, SOP development, business process systemisation, business valuation, due diligence, transaction advisory, financial structuring, business plans, strategies and project appraisals. His experience includes representation before authorities such as RBI, DGFT, SEBI, Registrar of Companies, tax authorities and appellate forums, together with drafting of strategic documents, settlements and compliance documentation. He is Partner in a legacy accounting firm (N D Kapur & Co., Chartered Accountants) operating in India for more than 90 years.",
    linkedin: "https://www.linkedin.com/in/umesh-gupta-498152117/",
  },
  {
    name: "Wamika Malik",
    photo: wamika,
    quals: "MSc in Computing -AI (NUS), Bachelors in Comp Eng (NUS)",
    role: "Resident Director",
    bio: "AI-focused Senior Software Engineer with 3 years of industry experience and a recently completed M.Sc. in Computer Science (AI specialization) from the National University of Singapore. Experienced in designing and implementing AI-driven solutions, including computer vision systems and LLM-powered applications, from problem definition to deployment. Strong in Python, data pipelines, and building end-to-end ML systems that align with real-world requirements. Skilled at translating business needs into scalable technical solutions and collaborating across teams to deliver measurable impact. Interested in applying AI and generative AI to solve complex business problems in financial services.",
    linkedin: "https://www.linkedin.com/in/wamika-malik-0173561a1/",
  },
];

const ADVISORS = [
  {
    name: "Navin Agrawal",
    photo: navin,
    quals: "B.Com.(H), FCA, DISA",
    role: "Independent Advisor",
    bio: "CA Navin Agarwal is a Fellow Member of the Institute of Chartered Accountants of India, a commerce graduate from Shri Ram College of Commerce, Delhi University, and a law graduate. He has more than 20 years of professional experience, covering both consulting and industry exposure. His areas of experience include direct tax advisory, indirect tax advisory, tax planning, assessment proceedings, litigation support before taxation authorities, audit and assurance support, and drafting of agreements and professional documents.",
    linkedin: "https://www.linkedin.com/in/navin-agarwal-665a81b/",
  },
  {
    name: "Piyush Kumar Gupta",
    photo: piyush,
    quals: "B.Com.(H), FCA, ACS, DISA, Cert. FEMA (ICSI), Cert. FAFD (ICAI)",
    role: "Independent Advisor",
    bio: "Chartered Accountant with more than 14 years of experience across audit-support environments, due diligence, internal financial controls, secretarial compliance, tax planning, assessment support, regulatory matters and business advisory. His experience includes support before corporate, tax and banking authorities, together with hands-on exposure to control reviews, financial documentation and compliance execution. He is Partner in a legacy accounting firm (N D Kapur & Co., Chartered Accountants) operating in India for more than 90 years.",
    linkedin: "https://www.linkedin.com/in/piyush-kumar-gupta-26103052/",
  },
  {
    name: "Mohit Goel",
    photo: mohit,
    quals: "B. Com.(H), FCA",
    role: "Independent Advisor",
    bio: "Chartered Accountant with more than 9 years of experience in the areas of GST Compliances and strategies and has handled various complex issues. He has also advised Clients on various GST matters and represented before various authorities. His expertise is in providing indirect tax advisory services to both foreign & domestic Clients. He is Partner in a legacy accounting firm (N D Kapur & Co., Chartered Accountants) operating in India for more than 90 years.",
    linkedin: "https://www.linkedin.com/in/mohit-kumar15/",
  },
  {
    name: "Puneet Gupta",
    photo: puneet,
    quals: "Chartered Accountant",
    role: "Independent Advisor",
    bio: "Qualified as a Chartered Accountant in 2020 and has gained practical experience across statutory audits, due diligence, internal audits, tax audits and assignments involving public and private sector organisations. He has developed exposure in Ind AS, IFRS, finance processes, compliance reviews and standard operating procedures. His work includes developing, reviewing and implementing SOPs, supporting internal control reviews, financial reporting processes and business-process documentation. He has served clients across various industries and brings a practical understanding of finance, compliance, reporting standards and process improvement.",
    linkedin: "https://www.linkedin.com/in/puneet-gupta-80636217a/",
  },
  {
    name: "Pinki Gupta",
    photo: pinki,
    quals: "Company Secretary",
    role: "Independent Advisor",
    bio: "Company secretary by profession. Significant exposure in the areas of work that includes statutory compliances, corporate governance, incorporations, conversions, due diligence, compounding matters, strike-off processes, mergers, investment-related compliances and representation support before regulatory authorities. She provides practical support in corporate documentation, compliance management, governance processes and regulatory filings for businesses requiring structured company law and secretarial support.",
    linkedin: "https://www.linkedin.com/in/cs-pinki-gupta-40ab0b58/",
  },
];

const ACCOUNTS_ADMIN = [
  {
    name: "Megha Singh",
    photo: megha,
    quals: "MBA Finance",
    role: "Accounts & Admn.",
    bio: "MBA Finance graduate with practical experience in accounting, bookkeeping, GST, TDS compliance and financial operations. Also experienced in inventory and stock management in the IT hardware sector. Proficient in Advanced Excel, Word and PowerPoint, with interest in AI tools and data analytics for technology-enabled finance solutions. Detail-oriented, adaptable and quick to learn, supporting assignments in finance, accounting, taxation, compliance and financial analysis.",
    linkedin: null,
  },
];

const CONSULTING_STEPS = [
  { step: "1", title: "Discovery", desc: "Understand the business background, current processes, key concerns, data availability and management expectations." },
  { step: "2", title: "Assessment and Diagnosis", desc: "Review workflows, documents, systems, reporting gaps, control gaps and operational pain points." },
  { step: "3", title: "Strategy Design", desc: "Design practical recommendations, reporting structures, process improvements, control points and implementation priorities." },
  { step: "4", title: "Implementation Planning", desc: "Create a step-wise action plan with responsibilities, timelines, documentation requirements and review checkpoints." },
  { step: "5", title: "Execution / Implementation", desc: "Support implementation through coordination, documentation, workflow set-up, reporting packs, dashboards and follow-up." },
  { step: "6", title: "Review and Performance Evaluation", desc: "Review progress against agreed objectives, identify unresolved gaps and evaluate practical outcomes." },
  { step: "7", title: "Optimisation and Continuous Improvement", desc: "Refine processes, update SOPs, strengthen controls and feed learning back into the next assessment cycle." },
];

// ─── THEME GRADIENT ────────────────────────────────────────────────────────────
const DARK_BG = `linear-gradient(155deg, #17305a 0%, #26446d 52%, #3b5a82 100%)`;

// ─── STYLES ──────────────────────────────────────────────────────────────────

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #0a1628;
    --navy-mid: #102040;
    --navy-light: #1a3055;
    --gold: #c9a84c;
    --gold-light: #e2c27a;
    --cream: #f8f4ee;
    --text: #1a1a2e;
    --muted: #6b7a8d;
    --border: #e2d9cc;
    --white: #ffffff;
    --sidebar-w: 270px;
    --header-h: 64px;
    --dark-gradient: linear-gradient(155deg, #17305a 0%, #26446d 52%, #3b5a82 100%);
    --dark-border: rgba(255,255,255,0.08);
  }

  html { scroll-behavior: smooth; }
  body {
    font-family: "Helvetica", Sans-serif;
    background: var(--cream);
    color: var(--text);
    line-height: 1.6;
    font-size: 16px;
  }
  h1,h2,h3,h4 { font-family: 'Cormorant Garamond', serif; line-height: 1.2; font-weight: 600; }

  /* ── HEADER ── */
  .header {
    position: fixed; top: 0; left: 0; right: 0;
    z-index: 100; height: var(--header-h);
    background: #173253;
    display: flex; align-items: center; padding: 0 24px; gap: 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    box-shadow: 0 10px 28px rgba(0,0,0,0.18), 0 1px 0 rgba(255,255,255,0.025) inset;
    backdrop-filter: blur(10px);
  }
  .header-logo { display:flex; align-items:center; gap:10px; cursor:pointer; flex-shrink:0; }
  .header-logo-mark { width:90px; border-radius:6px; display:flex; align-items:center; justify-content:center; }
  .header-logo-text { font-family:'Cormorant Garamond',serif; font-size:17px; font-weight:600; color:var(--cream); line-height:1.1; }
  .header-logo-sub { font-family:'DM Sans',sans-serif; font-size:10px; color:var(--gold); font-weight:400; letter-spacing:0.08em; text-transform:uppercase; }
  .header-tagline { font-size:11px; color:#8899aa; letter-spacing:0.06em; text-transform:uppercase; margin-left:auto; margin-right:16px; }
  .header-contact-btn {
    background: var(--gold); color:var(--navy);
    border:none; padding:8px 18px; border-radius:4px;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600;
    cursor:pointer; white-space:nowrap; flex-shrink:0; transition: background 0.2s;
  }
  .header-contact-btn:hover { background:var(--gold-light); }
  .sidebar-toggle { background:none; border:none; color:var(--cream); cursor:pointer; padding:6px; display:flex; align-items:center; flex-shrink:0; }

  .header-btn { margin-left: 30px; display: flex; flex-shrink: 0; }
  .hero-badge-btn {
    background: rgba(201,168,76,0.14); border: 1px solid rgba(201,168,76,0.32);
    color: #f0d58a; padding: 8px 16px; border-radius: 999px;
    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
    font-weight: 600; cursor: pointer; transition: all 0.25s ease;
    backdrop-filter: blur(6px); margin-right: 10px;
  }
  .hero-badge-btn:hover { background: rgba(201,168,76,0.24); transform: translateY(-1px); }
  .hero-badge-btn.active { background: rgba(201,168,76,0.28); }

  /* ── SIDEBAR ── */
  .sidebar-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.55); z-index:150; opacity:0; pointer-events:none; transition: opacity 0.25s; }
  .sidebar-overlay.open { opacity:1; pointer-events:all; }
  .sidebar {
    position:fixed; top:0; left:0; bottom:0; width:var(--sidebar-w);
    background: #173253; z-index:200;
    transform: translateX(-100%); transition: transform 0.28s cubic-bezier(.4,0,.2,1);
    display:flex; flex-direction:column; overflow:hidden;
    border-right: 1px solid rgba(255,255,255,0.06); box-shadow: 4px 0 24px rgba(0,0,0,0.22);
  }
  .sidebar.open { transform:translateX(0); }
  .sidebar-header {
    padding:20px 20px 16px; background: #173253;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
  }
  .sidebar-close { background:none; border:none; color:#8899aa; cursor:pointer; font-size:20px; line-height:1; transition: color 0.15s; }
  .sidebar-close:hover { color:var(--cream); }
  .sidebar-scroll { overflow-y:auto; flex:1; padding:12px 0 24px; }
  .sidebar-nav-item {
    display:flex; width:100%; background:none; border:none;
    text-align:left; cursor:pointer; color:var(--cream);
    padding:10px 20px; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:500;
    align-items:center; justify-content:space-between;
    transition: background 0.15s, color 0.15s; border-left: 3px solid transparent;
  }
  .sidebar-nav-item:hover { background: rgba(255,255,255,0.06); }
  .sidebar-nav-item.active { color:var(--gold); border-left-color:var(--gold); background:rgba(201,168,76,0.1); }
  .sidebar-nav-item .arrow { font-size:10px; color:#8899aa; transition: transform 0.2s; }
  .sidebar-nav-item.expanded .arrow { transform: rotate(90deg); }
  .sidebar-sub { overflow:hidden; transition: max-height 0.25s ease; background: rgba(0,0,0,0.18); }
  .sidebar-sub-item {
    display:flex; width:100%; background:none; border:none;
    text-align:left; cursor:pointer; padding:8px 20px 8px 36px;
    font-family:'DM Sans',sans-serif; font-size:13px; color:#aabbcc;
    transition: color 0.15s, background 0.15s; border-left: 3px solid transparent;
  }
  .sidebar-sub-item:hover { color:var(--cream); background:rgba(255,255,255,0.05); }
  .sidebar-sub-item.active { color:var(--gold); border-left-color:var(--gold); }
  .sidebar-divider { height:1px; background:rgba(255,255,255,0.08); margin:8px 20px; }

  /* ── MAIN LAYOUT ── */
  .page-wrap { padding-top: var(--header-h); min-height:100vh; }
  .section { padding:64px 0; }
  .section:nth-child(even) { background:var(--white); }
  .container { max-width:1100px; margin:0 auto; padding:0 32px; }

  /* ── HERO ── */
  .hero {
    position: relative; color: var(--cream);
    padding: 100px 0 88px; overflow: hidden; min-height: 580px;
  }
  .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center 30%; transform: scale(1.03); z-index: 0; }
  .hero-overlay {
    position: absolute; inset: 0; z-index: 1;
    background: linear-gradient(155deg, rgba(23,48,90,0.92) 0%, rgba(38,68,109,0.92) 52%, rgba(59,90,130,0.90) 100%);
  }
  .hero-content { position: relative; z-index: 2; }
  .hero::before {
    content: ''; position: absolute; top: -100px; right: -100px;
    width: 520px; height: 520px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.06);
    background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(140,170,220,0.04) 32%, transparent 72%);
    pointer-events: none;
  }
  .hero::after {
    content: ''; position: absolute; top: 30px; right: -30px;
    width: 300px; height: 300px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.045);
    background: radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%);
    pointer-events: none;
  }

  .hero-badge { background:rgba(201,168,76,0.15); border:1px solid rgba(201,168,76,0.3); color:var(--gold-light); padding:5px 14px; border-radius:20px; font-size:11px; letter-spacing:0.1em; text-transform:uppercase; margin-bottom:12px; font-weight:500; }

  @keyframes fadeFloatUp {
    0%   { opacity: 0; transform: translateY(28px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .hero-anim { opacity: 0; animation: fadeFloatUp 0.8s cubic-bezier(0.22, 0.9, 0.36, 1) forwards; }
  .hero-anim-d1 { animation-delay: 0.1s; }
  .hero-anim-d2 { animation-delay: 0.28s; }
  .hero-anim-d3 { animation-delay: 0.44s; }
  .hero-anim-d4 { animation-delay: 0.6s; }
  .hero-anim-d5 { animation-delay: 0.76s; }
  .hero-anim-d6 { animation-delay: 0.9s; }

  .hero h1 { font-size:clamp(2.2rem, 5vw, 3.4rem); color:var(--white); margin-bottom:8px; font-weight:700; }
  .hero-sub { font-size:clamp(1rem,2vw,1.2rem); color:var(--gold-light); margin-bottom:20px; font-family:'Cormorant Garamond',serif; font-weight:400; font-style:italic; }
  .hero-desc { font-size:15px; color:#aabbcc; max-width:620px; line-height:1.7; margin-bottom:36px; }
  .hero-stats { display:flex; gap:40px; flex-wrap:wrap; }
  .hero-stat-value { font-family:'Cormorant Garamond',serif; font-size:2rem; font-weight:700; color:var(--gold); }
  .hero-stat-label { font-size:11px; color:#8899aa; text-transform:uppercase; letter-spacing:0.08em; }
  .hero-btns { display:flex; gap:12px; flex-wrap:wrap; }

  .btn-primary { background:var(--gold); color:var(--navy); border:none; padding:12px 28px; border-radius:4px; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:600; cursor:pointer; transition:background 0.2s; }
  .btn-primary:hover { background:var(--gold-light); }
  .btn-outline { background:transparent; color:var(--cream); border:1px solid rgba(255,255,255,0.25); padding:12px 28px; border-radius:4px; font-family:'DM Sans',sans-serif; font-size:14px; font-weight:500; cursor:pointer; transition: border-color 0.2s, color 0.2s; }
  .btn-outline:hover { border-color:var(--gold); color:var(--gold); }

  /* ── SECTION TITLE ── */
  .section-title { font-size:clamp(1.6rem,3vw,2.4rem); color:var(--navy); margin-bottom:8px; }
  .section-title.light { color:var(--cream); }
  .section-subtitle { font-size:14px; color:var(--muted); margin-bottom:40px; max-width:640px; }
  .gold-line { width:48px; height:3px; background:linear-gradient(90deg,var(--gold),var(--gold-light)); margin-bottom:16px; border-radius:2px; }

  /* ── SERVICE GRID ── */
  .service-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:20px; align-items: stretch; }
  .service-card { background:var(--white); border:1px solid var(--border); border-radius:8px; padding:24px; cursor:pointer; transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s; display: flex; flex-direction: column; }
  .service-card:hover { box-shadow:0 8px 32px rgba(10,22,40,0.12); border-color:var(--gold); transform:translateY(-2px); }
  .service-card-icon { font-size:28px; margin-bottom:12px; }
  .service-card-title { font-family:'Cormorant Garamond',serif; font-size:17px; font-weight:600; color:var(--navy); margin-bottom:8px; }
  .service-card-desc { font-size:13px; color:var(--muted); line-height:1.6; flex:1; }
  .service-card-link { margin-top:14px; font-size:12px; color:var(--gold); font-weight:600; letter-spacing:0.04em; display:flex; align-items:center; gap:4px; }

  /* ── SERVICE DETAIL HEADER ── */
  .svc-detail-header { background: var(--dark-gradient); padding:48px 0 40px; color:var(--cream); position: relative; overflow: hidden; }
  .svc-detail-header::before { content:''; position:absolute; top:-80px; right:-80px; width:380px; height:380px; border-radius:50%; border: 1px solid rgba(255,255,255,0.05); background: radial-gradient(circle, rgba(255,255,255,0.025) 0%, transparent 70%); pointer-events:none; }
  .svc-detail-header h2 { font-size:clamp(1.6rem,3vw,2.2rem); color:var(--white); margin-bottom:12px; position:relative; }
  .svc-detail-intro { font-size:14px; color:#aabbcc; max-width:700px; line-height:1.7; position:relative; }

  .svc-items { margin-top:32px; display:flex; flex-direction:column; gap:1px; }
  .svc-item { background:var(--white); border:1px solid var(--border); border-radius:6px; overflow:hidden; transition:box-shadow 0.15s; }
  .svc-item:hover { box-shadow:0 4px 16px rgba(10,22,40,0.08); }
  .svc-item-q { padding:14px 20px; display:flex; align-items:center; justify-content:space-between; cursor:pointer; background:var(--white); }
  .svc-item-q span { font-weight:500; font-size:14px; color:var(--navy); }
  .svc-item-q .dot { width:8px; height:8px; border-radius:50%; background:var(--gold); flex-shrink:0; }
  .svc-item-a { padding:0 20px 14px 20px; font-size:13px; color:var(--muted); line-height:1.65; }
  .back-btn { background:none; border:none; cursor:pointer; color:var(--gold); font-size:13px; font-weight:600; display:flex; align-items:center; gap:6px; padding:0; margin-bottom:16px; font-family:'DM Sans',sans-serif; position:relative; }

  /* ── ABOUT ── */
  .about-grid { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:start; }
  @media(max-width:720px){ .about-grid { grid-template-columns:1fr; } }
  .corporate-table { width:100%; border-collapse:collapse; font-size:13px; }
  .corporate-table tr { border-bottom:1px solid var(--border); }
  .corporate-table td { padding:10px 8px; }
  .corporate-table td:first-child { color:var(--muted); white-space:nowrap; width:40%; font-weight:500; }
  .corporate-table td:last-child { color:var(--text); font-weight:400; }

  .values-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:16px; }
  .value-card { background:var(--white); border:1px solid var(--border); border-radius:8px; padding:20px; border-top: 3px solid var(--gold); }
  .value-card h4 { font-family:'Cormorant Garamond',serif; font-size:17px; color:var(--navy); margin-bottom:6px; }
  .value-card p { font-size:13px; color:var(--muted); line-height:1.6; }

  /* ── WHY SWOT ── */
  .section-dark { padding:64px 0; background: var(--dark-gradient); position: relative; overflow: hidden; }
  .section-dark::before { content:''; position:absolute; bottom:-60px; left:-60px; width:320px; height:320px; border-radius:50%; border: 1px solid rgba(255,255,255,0.04); background: radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%); pointer-events:none; }
  .why-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:20px; }
  .why-card { background:rgba(255,255,255,0.06); border:1px solid rgba(201,168,76,0.2); border-radius:8px; padding:24px; transition:background 0.2s; }
  .why-card:hover { background:rgba(201,168,76,0.1); }
  .why-card h4 { font-family:'Cormorant Garamond',serif; font-size:18px; color:var(--gold-light); margin-bottom:8px; }
  .why-card p { font-size:13px; color:#aabbcc; line-height:1.65; }

  /* ── INDUSTRIES ── */
  .industry-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; }
  .industry-card { background:var(--white); border:1px solid var(--border); border-radius:8px; padding:20px; display:flex; gap:16px; align-items:flex-start; }
  .industry-icon { font-size:28px; flex-shrink:0; }
  .industry-card h4 { font-family:'Cormorant Garamond',serif; font-size:16px; color:var(--navy); margin-bottom:4px; }
  .industry-card p { font-size:12.5px; color:var(--muted); line-height:1.55; }

  /* ── ENGAGEMENT / CONSULTING ── */
  .eng-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:20px; }
  .eng-card { background: var(--dark-gradient); border:1px solid rgba(255,255,255,0.08); border-radius:8px; padding:28px; color:var(--cream); position:relative; overflow:hidden; }
  .eng-card::before { content:''; position:absolute; bottom:-20px; right:-20px; width:80px; height:80px; border-radius:50%; border:1px solid rgba(201,168,76,0.15); }
  .eng-icon { font-size:32px; margin-bottom:16px; }
  .eng-card h3 { font-family:'Cormorant Garamond',serif; font-size:20px; color:var(--gold); margin-bottom:10px; }
  .eng-card p { font-size:13px; color:#aabbcc; line-height:1.65; }

  /* ── QUALITY ── */
  .quality-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:24px; }
  .quality-card { background:var(--white); border-radius:8px; padding:28px; border:1px solid var(--border); }
  .quality-card h3 { font-family:'Cormorant Garamond',serif; font-size:20px; color:var(--navy); margin-bottom:16px; padding-bottom:12px; border-bottom:2px solid var(--gold); }
  .quality-card ul { list-style:none; display:flex; flex-direction:column; gap:10px; }
  .quality-card li { font-size:13px; color:var(--muted); line-height:1.6; padding-left:16px; position:relative; }
  .quality-card li::before { content:'▸'; position:absolute; left:0; color:var(--gold); font-size:10px; top:2px; }

  /* ── PEOPLE CARDS ── */
  .people-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:24px; }
  .people-card { background:var(--white); border:1px solid var(--border); border-radius:8px; padding:24px; }

  .people-card-header { display:flex; align-items:flex-start; gap:16px; margin-bottom:14px; }

  .people-card-avatar {
    flex-shrink:0;
    width:88px; height:88px;
    border-radius:50%;
    overflow:hidden;
    background:var(--navy);
    display:flex; align-items:center; justify-content:center;
    font-family:'Cormorant Garamond',serif;
    font-size:28px; font-weight:600; color:var(--gold);
  }
  .people-card-avatar img { width:100%; height:100%; object-fit:cover; }

  .people-card-role { font-size:11px; letter-spacing:0.1em; text-transform:uppercase; color:var(--gold); font-weight:600; margin-bottom:3px; }
  .people-card h3 { font-family:'Cormorant Garamond',serif; font-size:21px; color:var(--navy); margin-bottom:3px; }
  .people-card-quals { font-size:12px; color:var(--muted); font-style:italic; }
  .people-card p { font-size:13px; color:var(--muted); line-height:1.65; }
  .people-card-linkedin { display:inline-flex; align-items:center; gap:5px; margin-top:12px; font-size:12px; color:var(--gold); font-weight:600; text-decoration:none; }
  .people-card-linkedin:hover { color:var(--gold-light); }

  /* ── CONSULTING STEPS ── */
  .steps-list { display:flex; flex-direction:column; gap:0; }
  .step-item { display:flex; gap:20px; padding:20px 0; border-bottom:1px solid var(--border); }
  .step-num { width:40px; height:40px; border-radius:50%; background:var(--dark-gradient); color:var(--gold); display:flex; align-items:center; justify-content:center; font-family:'Cormorant Garamond',serif; font-weight:700; font-size:18px; flex-shrink:0; }
  .step-content h4 { font-family:'Cormorant Garamond',serif; font-size:18px; color:var(--navy); margin-bottom:6px; }
  .step-content p { font-size:13px; color:var(--muted); line-height:1.65; }

  /* ── CONTACT ── */
  .contact-grid { display:grid; grid-template-columns:1fr 1fr; gap:48px; }
  @media(max-width:720px){ .contact-grid { grid-template-columns:1fr; } }
  .contact-office { background: var(--dark-gradient); color:var(--cream); border-radius:8px; padding:28px; border: 1px solid rgba(255,255,255,0.07); position: relative; overflow: hidden; }
  .contact-office::after { content:''; position:absolute; top:-30px; right:-30px; width:140px; height:140px; border-radius:50%; border: 1px solid rgba(201,168,76,0.1); pointer-events: none; }
  .contact-office h3 { font-family:'Cormorant Garamond',serif; font-size:20px; color:var(--gold); margin-bottom:16px; position:relative; }
  .contact-office p { font-size:13.5px; color:#aabbcc; line-height:1.7; position:relative; }
  .contact-office a { color:var(--gold-light); text-decoration:none; }

  .contact-form { display:flex; flex-direction:column; gap:16px; }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  @media(max-width:500px){ .form-row { grid-template-columns:1fr; } }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field label { font-size:12px; font-weight:600; color:var(--navy); text-transform:uppercase; letter-spacing:0.06em; }
  .form-field input, .form-field select, .form-field textarea { border:1px solid var(--border); border-radius:4px; padding:10px 12px; font-family:'DM Sans',sans-serif; font-size:14px; background:var(--white); color:var(--text); transition: border-color 0.15s; outline:none; }
  .form-field input:focus, .form-field select:focus, .form-field textarea:focus { border-color:var(--gold); }
  .form-field textarea { resize:vertical; min-height:90px; }
  .form-disclaimer { font-size:11.5px; color:var(--muted); line-height:1.6; padding:12px; background:#f5f2ec; border-radius:4px; }

  /* ── CAREERS ── */
  .careers-form { max-width:700px; display:flex; flex-direction:column; gap:16px; }

    /* ── INSIGHTS ── */
  .insights-placeholder { background:var(--white); border:1px dashed var(--border); border-radius:8px; padding:48px; text-align:center; color:var(--muted); }
  .insights-placeholder h3 { font-family:'Cormorant Garamond',serif; font-size:22px; color:var(--navy); margin-bottom:8px; }

  /* ── INSIGHTS / TESTIMONIALS ── */
  .insights-placeholder { background:var(--white); border:1px dashed var(--border); border-radius:8px; padding:48px; text-align:center; color:var(--muted); }
  .insights-placeholder h3 { font-family:'Cormorant Garamond',serif; font-size:22px; color:var(--navy); margin-bottom:8px; }

  /* ── FOOTER ── */
  .footer { background: var(--dark-gradient); color:#8899aa; padding:48px 0 24px; border-top:1px solid rgba(255,255,255,0.07); position: relative; overflow: hidden; }
  .footer::before { content:''; position:absolute; top:-100px; right:-100px; width:400px; height:400px; border-radius:50%; border: 1px solid rgba(255,255,255,0.04); background: radial-gradient(circle, rgba(255,255,255,0.015) 0%, transparent 70%); pointer-events:none; }
  .footer-grid { display:grid; grid-template-columns:2fr 1fr 1fr; gap:40px; margin-bottom:40px; position:relative; }
  @media(max-width:720px){ .footer-grid { grid-template-columns:1fr; } }
  .footer-brand h3 { font-family:'Cormorant Garamond',serif; font-size:22px; color:var(--cream); margin-bottom:6px; }
  .footer-brand p { font-size:13px; line-height:1.65; max-width:320px; }
  .footer-brand .tagline { color:var(--gold); font-style:italic; margin-bottom:12px; font-family:'Cormorant Garamond',serif; font-size:15px; }
  .footer-links h4 { color:var(--cream); font-size:13px; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; margin-bottom:14px; }
  .footer-links ul { list-style:none; display:flex; flex-direction:column; gap:8px; }
  .footer-links a { color:#8899aa; text-decoration:none; font-size:13px; transition:color 0.15s; cursor:pointer; }
  .footer-links a:hover { color:var(--gold); }
  .footer-bottom { border-top:1px solid rgba(255,255,255,0.07); padding-top:20px; font-size:12px; display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; position:relative; }

  .disclaimer-box {
    background: #173253; border-radius:6px; font-size:12px; color: var(--cream);
    line-height:1.65; margin-top:16px; border: 1px solid rgba(255,255,255,0.05);
    position:relative; overflow: hidden; padding: 10px 0;
  }
  .disclaimer-box::before, .disclaimer-box::after { content:''; position:absolute; top:0; bottom:0; width:40px; z-index:2; pointer-events:none; }
  .disclaimer-box::before { left:0; background: linear-gradient(to right, #173253, transparent); }
  .disclaimer-box::after  { right:0; background: linear-gradient(to left, #173253, transparent); }
  .disclaimer-scroll { display: inline-flex; white-space: nowrap; animation: disclaimer-roll linear infinite; }
  .disclaimer-scroll span { padding-right: 80px; }
  @keyframes disclaimer-roll { from { transform: translateX(0); } to { transform: translateX(-50%); } }

  /* ── MESSAGE BOX ── */
  .message-box { background: var(--dark-gradient); color:var(--cream); border-radius:10px; padding:40px; position:relative; overflow:hidden; margin-bottom:32px; border: 1px solid rgba(255,255,255,0.07); }
  .message-box p { font-size:15px; color:#aabbcc; line-height:1.85; margin-bottom:14px; text-align: justify; }
  .message-box p:last-child { margin-bottom:0; }

  /* ── VISION CARD ── */
  .vision-card { background: var(--dark-gradient); border-radius:8px; padding:32px; color:var(--cream); border: 1px solid rgba(255,255,255,0.07); position: relative; overflow: hidden; }
  .vision-card::after { content:''; position:absolute; bottom:-40px; right:-40px; width:160px; height:160px; border-radius:50%; border: 1px solid rgba(201,168,76,0.1); pointer-events:none; }

  /* ── SNAPSHOT TABLE ── */
  .snapshot-table { width:100%; border-collapse:collapse; font-size:13px; border-radius:8px; overflow:hidden; }
  .snapshot-table thead th { background:var(--navy); color:var(--gold); padding:12px 16px; text-align:left; font-family:'DM Sans',sans-serif; font-size:11px; letter-spacing:0.08em; text-transform:uppercase; }
  .snapshot-table tbody tr { border-bottom:1px solid var(--border); }
  .snapshot-table tbody tr:last-child { border-bottom:none; }
  .snapshot-table tbody td { padding:12px 16px; }
  .snapshot-table tbody td:first-child { color:var(--muted); font-weight:500; width:35%; }
  .snapshot-table tbody td:last-child { color:var(--text); }

  /* ── RESPONSIVE ── */
  @media(max-width:600px){
    .container { padding:0 16px; }
    .section { padding:48px 0; }
    .hero { padding:56px 0 48px; }
    .hero-stats { gap:24px; }
    .service-grid { grid-template-columns:1fr; }
  }
`;

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function SWOTSingapore() {
  const [page, setPage] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState({});

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  const navigate = (id) => { setPage(id); setSidebarOpen(false); };
  const toggleExpand = (id) => setExpandedNav(p => ({ ...p, [id]: !p[id] }));
  const isActivePage = (id) => {
    if (id === page) return true;
    const item = NAV.find(n => n.id === id);
    if (item?.sub) return item.sub.some(s => s.id === page);
    return false;
  };

  // ── SIDEBAR ──
  const Sidebar = () => (
    <>
      <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />
      <nav className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="header-logo">
            <div><img src={swotLogo} className="header-logo-mark"/></div>
            {/* <div style={{ width: 44, height: 44, background: "linear-gradient(135deg, var(--gold), var(--gold-light))", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 20, color: "var(--navy)" }}>S</div> */}
            <div>
              <div className="header-logo-text">SWOT</div>
              <div className="header-logo-sub">Management Solutions</div>
            </div>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
        </div>
        <div className="sidebar-scroll">
          {NAV.map(item => (
            <div key={item.id}>
              {item.sub ? (
                <>
                  <button
                    className={`sidebar-nav-item ${isActivePage(item.id) ? "active" : ""} ${expandedNav[item.id] ? "expanded" : ""}`}
                    onClick={() => toggleExpand(item.id)}
                  >
                    {item.label}
                    <span className="arrow">▶</span>
                  </button>
                  <div className="sidebar-sub" style={{ maxHeight: expandedNav[item.id] ? "1000px" : "0" }}>
                    {item.sub.map(s => (
                      <button key={s.id} className={`sidebar-sub-item ${page === s.id ? "active" : ""}`} onClick={() => navigate(s.id)}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <button className={`sidebar-nav-item ${isActivePage(item.id) ? "active" : ""}`} onClick={() => navigate(item.id)}>
                  {item.label}
                </button>
              )}
            </div>
          ))}
          <div className="sidebar-divider" />
          <button className="sidebar-nav-item" onClick={() => navigate("contact")} style={{ color: "var(--gold)" }}>
            📧 Contact Us
          </button>
        </div>
      </nav>
    </>
  );

  // ── HEADER ──
  const Header = () => (
    <header className="header">
      <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <div className="header-logo" onClick={() => navigate("home")}>
        <div><img src={swotLogo} className="header-logo-mark"/></div>
        {/* <div style={{ width: 44, height: 44, background: "linear-gradient(135deg, var(--gold), var(--gold-light))", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 20, color: "var(--navy)" }}>S</div> */}
        <div>
          <div className="header-logo-text">SWOT Singapore Private Limited</div>
          <div className="header-logo-sub">Singapore · Management Solutions & Operational Support</div>
        </div>
      </div>
      <div className="header-tagline">Singapore | India · Trusted Across Borders.</div>
      <div className="header-btn">
        <button className="hero-badge-btn" onClick={() => window.location.assign("https://swotmanagement.co.in")}>
          India Headquartered
        </button>
        <button className="hero-badge-btn active" onClick={() => window.location.assign("https://swotsingapore.com")}>
          Singapore APAC
        </button>
      </div>
      <button className="header-contact-btn" onClick={() => navigate("contact")}>Contact Us</button>
    </header>
  );

  // ── FOOTER ──
  const Footer = () => {
    const scrollRef = useRef(null);
    useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      const pxPerSec = Math.max(20, window.innerWidth * 0.01);
      const duration = (el.scrollWidth / 2) / pxPerSec;
      el.style.animationDuration = `${duration}s`;
    }, []);

    const text = "The information contained on this website is for general informational purposes only. It does not constitute legal, tax, investment, audit, assurance or other professional advice for any specific person or transaction. Readers should obtain advice based on their specific facts, jurisdiction and applicable law before acting on any information. Submission of an enquiry does not create a client relationship. Any engagement with SWOT is subject to scope finalisation, internal acceptance, conflict checks, applicable law, regulatory requirements and execution of agreed engagement terms. SWOT does not provide statutory audit, assurance, attestation, certification, banking, securities placement, investment management or other regulated services unless specifically authorised under applicable law and expressly covered in the engagement scope.";

    return (
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <h3>SWOT Singapore Private Limited</h3>
              <div className="tagline">Management Solutions | CFO Advisory | Operational Support | Business Transformation</div>
              <p>Singapore | India</p>
              <p style={{ marginTop: 8 }}>Email: <a href="mailto:secretarial@swotsingapore.com" style={{ color: "var(--gold-light)" }}>secretarial@swotsingapore.com</a></p>
            </div>
            <div className="footer-links">
              <h4>Company</h4>
              <ul>
                {["about-who", "about-vision", "about-founders", "about-board", "about-why", "consulting-method", "testimonials", "insights", "careers"].map(id => {
                  const flat = NAV.flatMap(n => n.sub ? n.sub : [n]);
                  const item = flat.find(i => i.id === id) || NAV.find(i => i.id === id);
                  return item ? <li key={id}><a onClick={() => navigate(id)}>{item.label}</a></li> : null;
                })}
              </ul>
            </div>
            <div className="footer-links">
              <h4>Services</h4>
              <ul>
                {SERVICES_OVERVIEW.slice(0, 8).map(s => (
                  <li key={s.id}><a onClick={() => navigate(s.id)}>{s.title.split(" ").slice(0, 3).join(" ")}&hellip;</a></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="disclaimer-box">
            <div className="disclaimer-scroll" ref={scrollRef}>
              <span>{text}</span>
              <span>{text}</span>
            </div>
          </div>
          <div className="footer-bottom">
            <span>© SWOT Singapore Private Limited All rights reserved.</span>
            <span>Singapore | India · APAC Regional Platform</span>
          </div>
        </div>
      </footer>
    );
  };

  // ── SHARED ──
  const SectionTitle = ({ title, sub, light }) => (
    <div>
      <div className="gold-line" />
      <h2 className={`section-title ${light ? "light" : ""}`}>{title}</h2>
      {sub && <p className="section-subtitle">{sub}</p>}
    </div>
  );

  // ── PAGES ──

  const HomePage = () => (
    <>
      {/* HERO */}
      <div className="hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${sgBg})` }} />
        {/* To use a photo: background: `url(${sgBg})`, backgroundSize: 'cover' */}
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="container">
            <h1 className="hero-anim hero-anim-d2">
              SWOT Singapore Private Limited
            </h1>
            <div className="hero-sub hero-anim hero-anim-d3">Management Solutions & Operational Support Services · Singapore | India</div>
            <p className="hero-desc hero-anim hero-anim-d4">
              Empowering businesses through efficient management solutions, operational excellence, and scalable support systems that drive sustainable success.
            </p>
            <div className="hero-stats hero-anim hero-anim-d5">
              <div>
                <div className="hero-stat-value">~30</div>
                <div className="hero-stat-label">Years of Experience</div>
              </div>
              <div>
                <div className="hero-stat-value">12+</div>
                <div className="hero-stat-label">Service Areas</div>
              </div>
              <div>
                <div className="hero-stat-value">SG + IN</div>
                <div className="hero-stat-label">Hybrid Delivery</div>
              </div>
            </div>
            <div style={{ marginTop: 36 }} className="hero-btns hero-anim hero-anim-d6">
              <button className="btn-primary" onClick={() => navigate("services")}>Explore Services</button>
              <button className="btn-outline" onClick={() => navigate("contact")}>Get in Touch</button>
            </div>
          </div>
        </div>
      </div>

      {/* Message from Team */}
      <div className="section" style={{ background: "var(--white)" }}>
        <div className="container">
          <SectionTitle title="A Message from Team" />
          <div className="message-box">
            <p>In today's fast-moving and interconnected business environment, organisations require more than conventional support. They need a dependable partner who can combine strategic understanding, operational discipline and execution capability across jurisdictions.</p>
            <p>Founded primarily by Partners of a legacy Accounting Firm, operating for more than 90 years in India, SWOT has been established to provide management solutions and operational support services company, serving businesses across Singapore, Asia – Pacific (APAC), India and cross-border markets. Our objective is to empower organisations with robust finance functions, effective internal controls and streamlined processes that enhance efficiency and reducing - administrative burden enabling management teams to focus on strategic growth.</p>
            <p>With a Singapore presence and a well established India delivery base backed by around 3 decades of operational and professional experience, SWOT brings together local business understanding, cross-border perspective and cost-efficient execution capability. Our delivery model is designed for businesses that require better visibility, timely reporting, improved turnaround, scalable back-office support and practical solutions aligned with their growth journey.</p>
            <p>Our service areas include finance function support, management reporting, Virtual CFO support, operational coordination, process improvement, SOP documentation, ERP and workflow transformation, regulatory documentation, payroll support and other scalable back-office solutions. We work with growing businesses, overseas investors, family-owned enterprises, professional firms and corporate groups that require reliable support across multiple functions and geographies.</p>
            <p>The establishment of SWOT in Singapore marks an important step towards building a broader regional platform for management support and advisory-led execution across Southeast Asia and the Asia-Pacific (APAC) region. Our focus remains clear -delivering practical, dependable and value-driven solutions that allow businesses to operate with greater confidence, stronger control and improved efficiency.</p>
            <p>We are grateful to our clients, partners and colleagues for their trust and support. We look forward to creating long-term value and supporting businesses in their journey towards sustainable growth.</p>
          </div>
        </div>
      </div>

      {/* Snapshot Facts */}
      <div className="section">
        <div className="container">
          <SectionTitle title="At a Glance" />
          <table className="snapshot-table">
            <thead>
              <tr>
                <th>Particular</th>
                <th>Content</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Business focus", "Management solutions, operational support, finance support and business transformation"],
                ["Locations", "Singapore and India"],
                ["Delivery model", "Hybrid Singapore-India operating model"],
                ["Experience base", "About 3 decades of operational and advisory support experience through the India office platform"],
                ["Core clients", "SMEs, Growth businesses, regional groups, start-ups, family businesses and companies requiring scalable support"],
                ["Engagement formats", "Project-based assignments, ongoing support, dedicated operational assistance and transformation mandates"],
              ].map(([k, v]) => (
                <tr key={k}><td>{k}</td><td>{v}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Services Preview */}
      <div className="section" style={{ background: "var(--white)" }}>
        <div className="container">
          <SectionTitle title="Our Service Areas" />
          <div className="service-grid">
            {SERVICES_OVERVIEW.slice(0, 6).map(s => (
              <div className="service-card" key={s.id} onClick={() => navigate(s.id)}>
                <div className="service-card-icon">{s.icon}</div>
                <div className="service-card-title">{s.title}</div>
                <div className="service-card-desc">{s.desc}</div>
                <div className="service-card-link">Learn more →</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <button className="btn-primary" onClick={() => navigate("services")}>View All Services</button>
          </div>
        </div>
      </div>

      {/* Why SWOT strip */}
      <div className="section-dark">
        <div className="container">
          <SectionTitle light title="Why Clients Choose SWOT" />
          <div className="why-grid">
            {WHY_SWOT.slice(0, 4).map((w, i) => (
              <div className="why-card" key={i}>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24 }}>
            <button className="btn-outline" onClick={() => navigate("about-why")}>Read More</button>
          </div>
        </div>
      </div>

      {/* Consulting Method Preview */}
      <div className="section">
        <div className="container">
          <SectionTitle title="Consulting Method" />
          <div className="steps-list">
            {CONSULTING_STEPS.slice(0, 4).map((s, i) => (
              <div className="step-item" key={i}>
                <div className="step-num">{s.step}</div>
                <div className="step-content">
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24 }}>
            <button className="btn-primary" onClick={() => navigate("consulting-method")}>See Full Framework</button>
          </div>
        </div>
      </div>
    </>
  );

  const AboutWhoPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>About SWOT Singapore</h2>
          <p className="svc-detail-intro">SWOT Singapore Private Limited is a Singapore-focused management solutions and operational support platform designed to assist businesses with finance support, administrative operations, strengthen reporting systems, process improvement and scalable back-office delivery to support sustainable business growth.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="about-grid">
            <div>
              <SectionTitle title="The Singapore–India Model" />
              <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.8, marginBottom: 20 }}>
                The Company works through a hybrid Singapore-India delivery model. Singapore provides client engagement -, business understanding and regional management, while the India delivery base provides execution depth, domain experience and cost-efficient scalability. This model allows businesses to access structured, reliable support services without significant increasing internal overheads.
              </p>
              <SectionTitle title="What Makes the Model Practical" />
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
                {[
                  ["Singapore + India Delivery", "Local coordination supported by scalable execution capability."],
                  ["Operational Experience", "Experience across finance, operations, controls, reporting and administrative functions."],
                  ["Cost Efficiency", "Flexible support without building unnecessary internal overheads."],
                  ["Process Orientation", "Documented workflows, defined accountability and periodic reporting."],
                  ["Flexible Engagements", "Project support, recurring support and dedicated assistance models."],
                ].map(([title, desc]) => (
                  <div key={title} style={{ padding: "12px 16px", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 6 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "var(--navy)", marginBottom: 4 }}>{title}</div>
                    <div style={{ fontSize: 13, color: "var(--muted)" }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <SectionTitle title="Professional Approach" />
              <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {[
                  "Understand the business context, operating model and reporting needs.",
                  "Define the scope, responsibilities, deliverables, timelines and information requirements.",
                  "Assess process gaps, documentation deficiencies, reporting inefficiencies, and internal control weaknesses.",
                  "Develop practical and scalable solutions through standard operating procedures (SOPs), reporting dashboards, workflows structures, controls framework and review mechanisms.",
                  "Support implementation through clearly defined responsibility matrices, structured reporting process and follow-up mechanism.",
                  "Monitor outcomes, evaluate effectiveness and refine the process for continuous improvement.",
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: 16, padding: "16px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--navy)", color: "var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.7, paddingTop: 4 }}>{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const VisionPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Vision, Mission, Values and Professional Approach</h2>
          <p className="svc-detail-intro">SWOT's management and operational support approach is guided by integrity, execution excellence, client centricity, confidentiality, accountability and strong regional outlook.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32, marginBottom: 48 }} className="about-grid">
            <div className="vision-card">
              <div className="gold-line" />
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: "var(--gold)", marginBottom: 16, position: "relative" }}>Our Vision</h3>
              <p style={{ fontSize: 14, color: "#aabbcc", lineHeight: 1.8, position: "relative" }}>To become a trusted Singapore-based management solutions partner, enabling businesses across Singapore, Asia – Pacific (APAC), India and cross-border markets through practical execution, stronger controls, scalable processes and cost-efficient delivery.</p>
            </div>
            <div className="vision-card">
              <div className="gold-line" />
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: "var(--gold)", marginBottom: 16, position: "relative" }}>Our Mission</h3>
              <p style={{ fontSize: 14, color: "#aabbcc", lineHeight: 1.8, position: "relative" }}>To help businesses improve visibility, reduce administrative burden and strengthen operational efficiency through reliable finance, reporting, process management, payroll, regulatory documentation and end-to-end back-office support services.</p>
            </div>
          </div>
          <SectionTitle title="Core Values" />
          <div className="values-grid">
            {[
              ["Integrity", "A firm commitment to ethical conduct, transparency and responsible business support."],
              ["Technical Excellence", "Structured delivery, timely completion and attention to detail in every assignment."],
              ["Client Centricity", "A service-led approach that keeps client priorities, timelines, confidentiality and business requirements at the centre of delivery."],
              ["Discretion", "Respect for client confidentiality, supported by access controls and secure work practices."],
              ["Accountability", "Clear ownership, scope clarity, defined deliverables and transparent communication."],
              ["Global Outlook", "Multi-jurisdictional fluency and a practical understanding of cross-border business requirements."],
            ].map(([name, desc]) => (
              <div className="value-card" key={name}>
                <h4>{name}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 48 }}>
            <SectionTitle title="How We Work" />
            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              {[
                "Understand the business context, jurisdictional footprint and commercial objective.",
                "Define the scope, deliverables, responsibilities, timeline and fee basis.",
                "Identify legal, tax, regulatory, accounting and operational dependencies.",
                "Prepare the required analysis, documentation, filings, process notes or deliverables.",
                "Review the work internally before client submission or implementation.",
                "Maintain confidentiality and records in accordance with internal protocols and applicable law.",
              ].map((step, i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "16px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--navy)", color: "var(--gold)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cormorant Garamond',serif", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>{i + 1}</div>
                  <div style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.7, paddingTop: 4 }}>{step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const getInitials = (name) =>
    name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const Avatar = ({ name, photo }) => (
    <div className="people-card-avatar">
      {photo ? (
        <img src={photo} alt={name} loading="lazy" />
      ) : (
        <span>{getInitials(name)}</span>
      )}
    </div>
  );

  const FoundersPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Founders / Promoters</h2>
          <p className="svc-detail-intro">SWOT's Promoters bring experience across finance leadership, CFO advisory, tax and regulatory support, internal controls, business process transformation, valuation, ERP/SAP transformation, treasury, working capital and operational improvement.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="people-grid">
            {FOUNDERS.map((f, i) => (
              <div className="people-card" key={i}>
              <div className="people-card-header">
                <Avatar name={f.name} photo={f.photo} />
                <div>
                  <div className="people-card-role">{f.role}</div>
                  <h3>{f.name}</h3>
                  <div className="people-card-quals">{f.quals}</div>
                </div>
              </div>
              <p>{f.bio}</p>
              {f.linkedin && (
                <a className="people-card-linkedin" href={f.linkedin} target="_blank" rel="noopener noreferrer">
                  🔗 LinkedIn Profile
                </a>
              )}
            </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const BoardPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Board of Directors & Advisors</h2>
          <p className="svc-detail-intro">Meet the Founders / Promoters & Board of Directors of SWOT, bringing experience across finance, tax, controls, operations, transformation, valuation and business advisory.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="people-grid">
            {BOARD.map((b, i) => (
              <div className="people-card" key={i}>
              <div className="people-card-header">
                <Avatar name={b.name} photo={b.photo} />
                <div>
                  <div className="people-card-role">{b.role}</div>
                  <h3>{b.name}</h3>
                  <div className="people-card-quals">{b.quals}</div>
                </div>
              </div>
              <p>{b.bio}</p>
              {b.linkedin && (
                <a className="people-card-linkedin" href={b.linkedin} target="_blank" rel="noopener noreferrer">
                  🔗 LinkedIn Profile
                </a>
              )}
            </div>
            ))}
          </div>

          <div style={{ marginTop: 56 }}>
            <SectionTitle title="Team of Advisors" />
            <div className="people-grid">
              {ADVISORS.map((a, i) => (
                <div className="people-card" key={i}>
                <div className="people-card-header">
                  <Avatar name={a.name} photo={a.photo} />
                  <div>
                    <div className="people-card-role">{a.role}</div>
                    <h3>{a.name}</h3>
                    <div className="people-card-quals">{a.quals}</div>
                  </div>
                </div>
                <p>{a.bio}</p>
                {a.linkedin && (
                  <a className="people-card-linkedin" href={a.linkedin} target="_blank" rel="noopener noreferrer">
                    🔗 LinkedIn Profile
                  </a>
                )}
              </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 56 }}>
            <SectionTitle title="Accounts & Admn." />
            <div className="people-grid">
              {ACCOUNTS_ADMIN.map((a, i) => (
                <div className="people-card" key={i}>
                  <div className="people-card-header">
                    <Avatar name={a.name} photo={a.photo} />
                    <div>
                      <div className="people-card-role">{a.role}</div>
                      <h3>{a.name}</h3>
                      <div className="people-card-quals">{a.quals}</div>
                    </div>
                  </div>
                  <p>{a.bio}</p>
                  {a.linkedin && (
                    <a className="people-card-linkedin" href={a.linkedin} target="_blank" rel="noopener noreferrer">
                      🔗 LinkedIn Profile
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const WhySwotPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Why SWOT</h2>
          <p className="svc-detail-intro">In a market with no shortage of professional service providers, the choice of advisor matters. The following define what we believe distinguishes SWOT — and why our clients return to us, engagement after engagement, year after year.</p>
        </div>
      </div>
      <div className="section-dark">
        <div className="container">
          <div className="why-grid">
            {WHY_SWOT.map((w, i) => (
              <div className="why-card" key={i}>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const QualityPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Quality, Integrity & Confidentiality</h2>
          <p className="svc-detail-intro">SWOT's reputation has been built on the trust placed in the company by every client we serve. That trust is protected through a formal framework of confidentiality, Integrity and quality control protocols observed across every engagement, every team member and every jurisdiction in which we operate.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="quality-grid">
            <div className="quality-card">
              <h3>Confidentiality</h3>
              <ul>
                <li>Client information is treated as strictly confidential and is accessed strictly on a need-to-know basis.</li>
                <li>All professional staff are bound by formal confidentiality undertakings as a condition of engagement with the firm.</li>
                <li>Engagement-specific data rooms, encrypted communications and access-controlled storage are used for sensitive information.</li>
                <li>Client data is retained, archived and destroyed in accordance with applicable law and professional retention requirements.</li>
              </ul>
            </div>
            <div className="quality-card">
              <h3>Quality Control</h3>
              <ul>
                <li>Every deliverable is subject to multi-level review by qualified professionals before issuance to the client.</li>
                <li>Engagement files, working papers and supporting documentation are maintained in accordance with applicable standards.</li>
                <li>Periodic internal quality reviews validate adherence to company -wide methodology and risk-management policies.</li>
                <li>Continuous monitoring of regulatory updates ensures that client advisory remains current and accurate.</li>
              </ul>
            </div>
            <div className="quality-card">
              <h3>Data Protection</h3>
              <ul>
                <li>The Company complies with applicable data protection laws, including the Digital Personal Data Protection Act, 2023 (India).</li>
                <li>Client personal data is processed only for legitimate professional purposes and within applicable legal frameworks.</li>
                <li>Cloud platforms and software tools used by the firm are selected with reference to security certifications, data residency considerations and contractual data protection commitments.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const ServicesPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Services</h2>
          <p className="svc-detail-intro">SWOT provides integrated management solutions and operational support for businesses requiring structured finance, reporting, administrative and process support. The services are designed to improve visibility, reduce internal workload, strengthen documentation and support business decisions through better information flow.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="service-grid">
            {SERVICES_OVERVIEW.map(s => (
              <div className="service-card" key={s.id} onClick={() => navigate(s.id)}>
                <div className="service-card-icon">{s.icon}</div>
                <div className="service-card-title">{s.title}</div>
                <div className="service-card-desc">{s.desc}</div>
                <div className="service-card-link">Explore →</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const ServiceDetailPage = ({ id }) => {
    const [openItem, setOpenItem] = useState(null);
    const detail = SERVICE_DETAILS[id];
    if (!detail) return null;
    return (
      <>
        <div className="svc-detail-header">
          <div className="container">
            <button className="back-btn" onClick={() => navigate("services")}>← Back to Services</button>
            <div className="gold-line" />
            <h2>{detail.title}</h2>
            <p className="svc-detail-intro">{detail.intro}</p>
          </div>
        </div>
        <div className="section">
          <div className="container">
            <div className="svc-items">
              {detail.items.map(([title, desc], i) => (
                <div className="svc-item" key={i}>
                  <div className="svc-item-q" onClick={() => setOpenItem(openItem === i ? null : i)}>
                    <span>{title}</span>
                    <div className="dot" />
                  </div>
                  {openItem === i && <div className="svc-item-a">{desc}</div>}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 32, textAlign: "center" }}>
              <button className="btn-primary" onClick={() => navigate("contact")}>Enquire About This Service</button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const IndustriesPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Industries Served</h2>
          <p className="svc-detail-intro">SWOT's management solutions and operational support services are relevant across sectors where finance visibility, process discipline, reporting systems and administrative support are critical. The following sector grouping is illustrative and should not be presented as a client list or endorsement.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="industry-grid">
            {INDUSTRIES.map((ind, i) => (
              <div className="industry-card" key={i}>
                <div className="industry-icon">{ind.icon}</div>
                <div>
                  <h4>{ind.group}</h4>
                  <p>{ind.sectors}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const ConsultingMethodPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Consulting Method and Engagement Framework</h2>
          <p className="svc-detail-intro">SWOT follows a structured consulting methodology designed to move systematically from understanding the issue to implementing practical solutions and improving them over time. The framework keeps the engagement focused, measurable and aligned with business priorities.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="steps-list">
            {CONSULTING_STEPS.map((s, i) => (
              <div className="step-item" key={i}>
                <div className="step-num">{s.step}</div>
                <div className="step-content">
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <button className="btn-primary" onClick={() => navigate("contact")}>Start a Conversation</button>
          </div>
        </div>
      </div>
    </>
  );

  const TestimonialsPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line" />
          <h2>Client Testimonials</h2>
          <p className="svc-detail-intro">Client feedback reflects the practical value of structured finance support, process improvement, reporting visibility and operational coordination. Testimonials are published after client approval and are limited to factual feedback on the nature of service and support provided.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="insights-placeholder">
            <h3>Testimonials Coming Soon</h3>
            <p style={{ fontSize: 14, marginBottom: 8 }}>Client feedback on SWOT's management solutions, finance support, process improvement and operational support services will be published here after written client consent.</p>
            <p style={{ fontSize: 12, marginTop: 16, color: "#9aabb5" }}>Testimonials are limited to factual feedback and published only with explicit written approval from clients.</p>
          </div>
        </div>
      </div>
    </>
  );

  const InsightsPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Insights & Professional Updates</h2>
          <p className="svc-detail-intro">SWOT publishes professional updates, articles, briefers and resource notes to assist readers in keeping track of relevant professional and regulatory developments.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="insights-placeholder">
            <h3>Coming Soon</h3>
            <p style={{fontSize:14,marginBottom:8}}>Professional updates on Tax, GST, FEMA, RBI, Corporate Compliance, Transactions, Accounting, Virtual CFO and Regulatory matters will be published here.</p>
            <p style={{fontSize:12,marginTop:16,color:"#9aabb5"}}>The materials in this section are for general informational purposes only and do not constitute professional advice.</p>
          </div>
        </div>
      </div>
    </>
  );

  const CareersPage = () => {
    const [sent, setSent] = useState(false);
    return (
      <>
        <div className="svc-detail-header">
          <div className="container">
            <div className="gold-line" />
            <h2>Careers</h2>
            <p className="svc-detail-intro">SWOT offers career opportunities for professionals and support team members interested in finance support, operations, management reporting, accounting, payroll, compliance documentation, ERP coordination, business process improvement and advisory assignments.</p>
          </div>
        </div>
        <div className="section">
          <div className="container">
            <div style={{ marginBottom: 40 }}>
              <SectionTitle title="Learning and Work Environment" />
              <p style={{ fontSize: 14, color: "var(--muted)", lineHeight: 1.8, maxWidth: 700, marginBottom: 32 }}>
                Team members work on practical assignments involving documentation, reporting, reconciliations, data analysis, process mapping, SOPs, dashboards, regulatory support, client coordination and implementation follow-up. The work environment values confidentiality, accountability, documentation, timely communication and practical problem-solving.
              </p>
              <SectionTitle title="Suggested Role Categories" />
              <div className="service-grid" style={{ marginTop: 16 }}>
                {[
                  ["Finance and MIS Professionals", "Support budgeting, reporting packs, dashboards, variance analysis and financial summaries."],
                  ["Accounting and Bookkeeping Team", "Support bookkeeping, reconciliations, closing schedules and compliance-linked finance processes."],
                  ["Operations and Shared Services Team", "Support workflow tracking, vendor coordination, documentation processing and managed support desks."],
                  ["ERP and Process Transformation Team", "Support process mapping, ERP coordination, automation and control documentation."],
                  ["Regulatory Documentation Team", "Support filings, replies, registers, compliance trackers and regulatory documentation."],
                  ["Interns / Trainees", "Support research, documentation, data preparation, reporting templates and assignment coordination."],
                ].map(([title, desc]) => (
                  <div className="service-card" key={title} style={{ cursor: "default" }}>
                    <div className="service-card-title">{title}</div>
                    <div className="service-card-desc">{desc}</div>
                  </div>
                ))}
              </div>
            </div>
            {sent ? (
              <div style={{ padding: 40, textAlign: "center", background: "var(--white)", borderRadius: 8, border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: "var(--navy)", marginBottom: 8 }}>Application Received</h3>
                <p style={{ color: "var(--muted)", fontSize: 14 }}>Thank you for your interest in SWOT Singapore. We will review your application and respond accordingly.</p>
              </div>
            ) : (
              <div className="careers-form">
                <SectionTitle title="Apply to SWOT" sub="Candidates can submit their resume through the website career form or by email to the designated HR/careers email address." />
                <div className="form-row">
                  <div className="form-field"><label>Name *</label><input type="text" placeholder="Your full name" /></div>
                  <div className="form-field"><label>Email *</label><input type="email" placeholder="your@email.com" /></div>
                </div>
                <div className="form-row">
                  <div className="form-field"><label>Phone</label><input type="tel" placeholder="+65 or +91 number" /></div>
                  <div className="form-field"><label>Current City</label><input type="text" placeholder="Singapore / India / Other" /></div>
                </div>
                <div className="form-row">
                  <div className="form-field"><label>Qualification</label><input type="text" placeholder="CA / MBA / CMA / BBA etc." /></div>
                  <div className="form-field"><label>Position Applied For</label><input type="text" placeholder="e.g. Finance Analyst" /></div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label>Experience</label>
                    <select>
                      <option value="">Select experience</option>
                      <option>Fresher / Trainee</option>
                      <option>1–3 Years</option>
                      <option>3–6 Years</option>
                      <option>6–10 Years</option>
                      <option>10+ Years</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Preferred Work Area</label>
                    <select>
                      <option value="">Select area</option>
                      <option>Finance / Operations</option>
                      <option>ERP</option>
                      <option>Compliance</option>
                      <option>HR</option>
                      <option>Advisory</option>
                    </select>
                  </div>
                </div>
                <div className="form-field">
                  <label>Resume Upload</label>
                  <input type="file" />
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <input type="checkbox" style={{ marginTop: 3 }} />
                  <label style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.6 }}>
                    I consent to SWOT processing my application data for the purpose of reviewing this application.
                  </label>
                </div>
                <div className="form-disclaimer">
                  Personal information submitted through contact, testimonial or career forms is used for responding to the relevant enquiry, reviewing the application, obtaining approvals and carrying out related administrative purposes. Users should not submit confidential information until an engagement is formally accepted.
                </div>
                <div>
                  <button className="btn-primary" onClick={() => setSent(true)}>Submit Application</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const ContactPage = () => {
    const [sent, setSent] = useState(false);
    return (
      <>
        <div className="svc-detail-header">
          <div className="container">
            <div className="gold-line" />
            <h2>Contact Us</h2>
            <p className="svc-detail-intro">For professional and business enquiries, please write to SWOT at secretarial@swotsingapore.com. The relevant team member will respond based on the nature of the enquiry and proposed scope of work.</p>
          </div>
        </div>
        <div className="section">
          <div className="container">
            <div className="contact-grid">
              <div>
                <div className="contact-office" style={{ marginBottom: 20 }}>
                  <h3>Singapore Office</h3>
                  <p>SWOT Singapore Private Limited<br />
                    20 CECIL STREET, #05-03, PLUS, SINGAPORE 049705</p>
                  <p style={{ marginTop: 12 }}>Email: <a href="mailto:secretarial@swotsingapore.com">secretarial@swotsingapore.com</a></p>
                  <p style={{ marginTop: 8, color: "#8899aa", fontSize: 12.5 }}>
                    Phone: to be updated.
                  </p>
                  <p style={{ marginTop: 8 }}>
                    <a href="https://www.linkedin.com/company/swot-singapore-pte-ltd" target="_blank" rel="noopener noreferrer">LinkedIn →</a>
                  </p>
                </div>
                <div className="contact-office">
                  <h3>India Company</h3>
                  <p>SWOT Management Solutions Private Limited<br />
                    II Floor, The Great Eastern Centre, 70, Nehru Place<br />
                    Behind IFCI Tower, New Delhi - 110019, India</p>
                  <p style={{ marginTop: 12 }}>Email: <a href="mailto:secretarial@swotmanagement.co.in">secretarial@swotmanagement.co.in</a></p>
                  <p style={{ marginTop: 8 }}>
                    <a href="https://www.linkedin.com/company/swot-management-solution-private-limited/" target="_blank" rel="noopener noreferrer">LinkedIn →</a>
                  </p>
                </div>
              </div>
              <div>
                {sent ? (
                  <div style={{ padding: 40, textAlign: "center", background: "var(--white)", borderRadius: 8, border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                    <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: "var(--navy)", marginBottom: 8 }}>Enquiry Received</h3>
                    <p style={{ color: "var(--muted)", fontSize: 14 }}>Thank you for reaching out. We will review your enquiry and respond subject to scope evaluation and resource availability.</p>
                  </div>
                ) : (
                  <div className="contact-form">
                    <SectionTitle title="Send an Enquiry" />
                    <div className="form-row">
                      <div className="form-field"><label>Name *</label><input type="text" placeholder="Full name" /></div>
                      <div className="form-field"><label>Company</label><input type="text" placeholder="Company / organisation" /></div>
                    </div>
                    <div className="form-row">
                      <div className="form-field"><label>Email *</label><input type="email" placeholder="your@email.com" /></div>
                      <div className="form-field"><label>Phone</label><input type="tel" placeholder="+65 / +91" /></div>
                    </div>
                    <div className="form-row">
                      <div className="form-field"><label>Country / Jurisdiction</label><input type="text" placeholder="e.g. Singapore, India" /></div>
                      <div className="form-field">
                        <label>Service Area of Interest</label>
                        <select>
                          <option value="">Select service area</option>
                          {SERVICES_OVERVIEW.map(s => <option key={s.id}>{s.title}</option>)}
                          <option>General Enquiry</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Brief Enquiry *</label>
                      <textarea placeholder="Please describe your requirement briefly..." />
                    </div>
                    <div className="form-disclaimer">
                      Submission of an enquiry does not create a client relationship. Any engagement with SWOT is subject to scope finalisation, internal acceptance, conflict checks, applicable law, regulatory requirements and execution of agreed engagement terms. Personal information submitted through this form is used for responding to the relevant enquiry and related administrative purposes. Users should not submit confidential information until an engagement is formally accepted.
                    </div>
                    <div>
                      <button className="btn-primary" onClick={() => setSent(true)}>Send Enquiry</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── RENDER PAGE ──
  const renderPage = () => {
    if (page === "home") return <HomePage />;
    if (page === "about-who") return <AboutWhoPage />;
    if (page === "about-vision") return <VisionPage />;
    if (page === "about-founders") return <FoundersPage />;
    if (page === "about-board") return <BoardPage />;
    if (page === "about-why") return <WhySwotPage />;
    if (page === "about-quality") return <QualityPage />;
    if (page === "services") return <ServicesPage />;
    if (SERVICE_DETAILS[page]) return <ServiceDetailPage id={page} />;
    if (page === "industries") return <IndustriesPage />;
    if (page === "consulting-method") return <ConsultingMethodPage />;
    if (page === "testimonials") return <TestimonialsPage />;
    if (page === "insights") return <InsightsPage />;
    if (page === "careers") return <CareersPage />;
    if (page === "contact") return <ContactPage />;
    return <HomePage />;
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="page-wrap">
        {renderPage()}
        <Footer />
      </div>
    </div>
  );
}